import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { prepareInlineCheckout, clearInlineCheckoutData } from '../../store/slices/paymentSlice';
import { clearCart, clearServerCart, fetchCart } from '../../store/slices/cartSlice';
import { fetchWithAuth } from '../../utils/authUtils';
import BASE_URL from '../../config';
import { usePaddle } from '../../hooks/usePaddle';
import styles from './PaddleCheckout.module.css';
import './PaddleGlobalOverrides.css';
import { buildOrderAnalyticsProps, capture } from '../../analytics';

const PaddleCheckout = ({ orderId, orderData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { inlineCheckoutStatus, inlineCheckoutData, error } = useSelector((state) => state.payment);
  const [isProcessing, setIsProcessing] = useState(false);
  const [backendError, setBackendError] = useState(null);
  const { isLoaded, isInitialized, createCheckout, fixIframePositioning } = usePaddle();
  const orderAnalyticsProps = buildOrderAnalyticsProps(orderData || {});

  // Handle payment success
  const handlePaymentSuccess = (data) => {
    console.log('Payment successful:', data);
    capture("paddle_checkout_completed", {
      ...orderAnalyticsProps,
      order_id: orderId,
      transaction_id: data?.transaction_id || data?.id,
    });
    setIsProcessing(false);
    dispatch(clearCart());

    // Сохраняем orderId для подтверждения оплаты на profile странице
    localStorage.setItem('payment_success', 'true');
    localStorage.setItem('payment_order_id', String(orderId));
    localStorage.setItem('payment_data', JSON.stringify(data));
    // Навигация — confirm-payment вызовется из ProfileAssets (стабильный компонент)
    navigate('/profile?payment_success=1');
  };

  // Prepare inline checkout
  const handlePrepareInlineCheckout = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    setBackendError(null);

    try {
      // Get checkout data from backend
      const resultAction = await dispatch(prepareInlineCheckout({
        order_id: orderId,
        address: orderData?.address || {
          // Provide default address data if none provided
          first_line: "Default Address",
          city: "Default City", 
          postal_code: "12345",
          country_code: "US",
          region: "Default Region"
        }
      }));

      const { payload } = resultAction;

      // Check if the backend returned an error
      if (resultAction.error) {
        setBackendError(payload?.error || 'Backend error occurred');
        capture("paddle_checkout_error", {
          ...orderAnalyticsProps,
          order_id: orderId,
          stage: "prepare",
          error_message: payload?.error || 'Backend error occurred',
        });
        setIsProcessing(false);
        return;
      }

      // Check if we have the required data
      if (!payload) {
        setBackendError('No response from server');
        capture("paddle_checkout_error", {
          ...orderAnalyticsProps,
          order_id: orderId,
          stage: "prepare",
          error_message: 'No response from server',
        });
        setIsProcessing(false);
        return;
      }

      // Store the checkout data for inline checkout
      // The inlineCheckoutData will be set by the Redux slice

      // The inline checkout will be opened automatically by useEffect when data is available
      setIsProcessing(false);

    } catch (error) {
      setBackendError(error.message || 'Failed to prepare inline checkout');
      capture("paddle_checkout_error", {
        ...orderAnalyticsProps,
        order_id: orderId,
        stage: "prepare",
        error_message: error.message || 'Failed to prepare inline checkout',
      });
      setIsProcessing(false);
    }
  };

  // Open inline checkout
  const handleOpenInlineCheckout = async (checkoutData) => {
    if (!isLoaded) {
      setBackendError('Payment system not ready - Paddle not loaded');
      capture("paddle_checkout_error", {
        ...orderAnalyticsProps,
        order_id: orderId,
        stage: "open",
        error_message: 'Payment system not ready - Paddle not loaded',
      });
      setIsProcessing(false);
      return;
    }

    if (!isInitialized) {
      setBackendError('Payment system not ready - Paddle not initialized');
      capture("paddle_checkout_error", {
        ...orderAnalyticsProps,
        order_id: orderId,
        stage: "open",
        error_message: 'Payment system not ready - Paddle not initialized',
      });
      setIsProcessing(false);
      return;
    }

    if (!window.Paddle) {
      setBackendError('Payment system not ready - Paddle object not found');
      capture("paddle_checkout_error", {
        ...orderAnalyticsProps,
        order_id: orderId,
        stage: "open",
        error_message: 'Payment system not ready - Paddle object not found',
      });
      setIsProcessing(false);
      return;
    }

    try {
      // Wait for the next render cycle to ensure the container is available
      await new Promise(resolve => setTimeout(resolve, 100));

      // Ensure the checkout container exists and is properly set up
      const container = document.getElementById('paddle-checkout-container');
      
      if (!container) {
        setBackendError('Payment form container not found');
        capture("paddle_checkout_error", {
          ...orderAnalyticsProps,
          order_id: orderId,
          stage: "open",
          error_message: 'Payment form container not found',
        });
        setIsProcessing(false);
        return;
      }

      // Clear the container to ensure it's ready for Paddle
      container.innerHTML = '';
      
      // Ensure the container has proper styling
      container.style.position = 'relative';
      container.style.width = '100%';
      container.style.minHeight = '450px';
      container.style.overflow = 'visible';
      container.style.borderRadius = '8px';
      container.style.zIndex = '1';
      
      // Add a loading indicator to the container
      container.innerHTML = '<div style="text-align: center; padding: 40px; color: #666;">Loading payment form...</div>';

      // Wait a moment to ensure the container is ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Open Paddle inline checkout with existing transaction
      await createCheckout({
        mode: 'inline', // Specify inline mode
        container: '#paddle-checkout-container', // Specify the container
        transactionId: checkoutData.transaction_id, // Use the transaction ID from backend
        openCallback: () => {
          capture("paddle_checkout_opened", {
            ...orderAnalyticsProps,
            order_id: orderId,
            transaction_id: checkoutData.transaction_id,
          });
        },
        successCallback: (data) => {
          handlePaymentSuccess(data);
        },
        closeCallback: () => {
          capture("paddle_checkout_closed", {
            ...orderAnalyticsProps,
            order_id: orderId,
            transaction_id: checkoutData.transaction_id,
          });
          setIsProcessing(false);
        },
        errorCallback: (error) => {
          setBackendError(error.message || 'Payment failed');
          capture("paddle_checkout_error", {
            ...orderAnalyticsProps,
            order_id: orderId,
            transaction_id: checkoutData.transaction_id,
            stage: "checkout",
            error_message: error.message || 'Payment failed',
          });
          setIsProcessing(false);
        }
      });

      

    } catch (error) {
      setBackendError(error.message || 'Failed to open payment form');
      capture("paddle_checkout_error", {
        ...orderAnalyticsProps,
        order_id: orderId,
        transaction_id: checkoutData?.transaction_id,
        stage: "open",
        error_message: error.message || 'Failed to open payment form',
      });
      setIsProcessing(false);
    }
  };

  // Clear checkout data on unmount
  useEffect(() => {
    return () => {
      dispatch(clearInlineCheckoutData());
    };
  }, [dispatch]);

  // Automatically prepare inline checkout when component mounts and Paddle is ready
  useEffect(() => {
    if (isLoaded && isInitialized && !inlineCheckoutData && !isProcessing && !backendError) {
      console.log('Paddle ready, automatically preparing inline checkout...');
      handlePrepareInlineCheckout();
    }
  }, [isLoaded, isInitialized, inlineCheckoutData, isProcessing, backendError]);

  // Handle inline checkout data when it becomes available
  useEffect(() => {
    if (inlineCheckoutData && isLoaded && isInitialized) {
      handleOpenInlineCheckout(inlineCheckoutData);
    }
  }, [inlineCheckoutData, isLoaded, isInitialized]);

  // Monitor and fix iframe positioning after Paddle renders
  useEffect(() => {
    if (!inlineCheckoutData) return;
    
    // Use the fixIframePositioning function from the hook
    const cleanup = fixIframePositioning('#paddle-checkout-container');

    return cleanup;
  }, [inlineCheckoutData, fixIframePositioning]);

  return (
    <div className={styles.paddleCheckout}>
      <div className={styles.checkoutContainer}>
        <div className={styles.inlineCheckoutContainer}>
          {/* Container for inline checkout - always render this */}
          <div className={styles.securityBadge}>
            <span>🔒 Secure Payment</span>
          </div>
          <h3>Complete Your Purchase</h3>
          <p>Your payment is being processed securely by Paddle.</p>
          <div 
            id="paddle-checkout-container" 
            className={styles.checkoutFrame}
            style={{
              position: 'relative',
              width: '100%',
              minHeight: '700px',
              overflow: 'visible',
              borderRadius: '8px',
              zIndex: 1
            }}
          >
            {/* Show loading state while preparing checkout */}
            {(!inlineCheckoutData || isProcessing) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#666',
                fontSize: '14px'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #667eea',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '10px'
                }}></div>
                {isProcessing ? 'Preparing payment form...' : 'Loading payment system...'}
              </div>
            )}
          </div>
          
          {(inlineCheckoutStatus === 'failed' || backendError) && (
            <div className={styles.error}>
              <p>Payment setup failed. Please refresh the page to try again.</p>
              {(backendError || error) && (
                <p className={styles.errorDetails}>
                  Error: {backendError || error}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaddleCheckout;
